import { Link } from 'react-router-dom';
import { Shield, ArrowLeft } from 'lucide-react';

export default function Privacy() {
  return (
    <div className="app-container">
      <div className="app-wrapper">
        <div className="app-card legal-page">
          <div className="app-header">
            <h1>
              <Shield className="header-icon" />
              Privacy Policy
            </h1>
            <p>Last updated: March 2026</p>
          </div>

          <div className="legal-content">
            <section>
              <h2>1. Introduction</h2>
              <p>
                WiredCop LLC ("Company", "we", "us", or "our") respects your privacy and is
                committed to protecting your personal data. This Privacy Policy explains how
                we collect, use, and safeguard your information when you use the Ride Share
                Checker service ("Service").
              </p>
            </section>

            <section>
              <h2>2. Information We Collect</h2>

              <h3>2.1 Account Information</h3>
              <p>When you create an account, we collect:</p>
              <ul>
                <li>Email address</li>
                <li>Password (encrypted)</li>
                <li>Subscription status and billing information (processed by Stripe)</li>
              </ul>

              <h3>2.2 Usage Information</h3>
              <p>We automatically collect:</p>
              <ul>
                <li>License plate numbers you search (for usage tracking only)</li>
                <li>Number of lookups performed</li>
                <li>IP address</li>
                <li>Device and browser information</li>
                <li>Access times and dates</li>
              </ul>

              <h3>2.3 Images</h3>
              <p>
                When you use the camera or upload feature, images are processed to extract
                license plate information. <strong>Images are not stored on our servers</strong>
                and are only used temporarily for plate recognition.
              </p>
            </section>

            <section>
              <h2>3. How We Use Your Information</h2>
              <p>We use the collected information to:</p>
              <ul>
                <li>Provide and maintain the Service</li>
                <li>Process your subscription and payments</li>
                <li>Enforce usage limits based on your subscription tier</li>
                <li>Improve and optimize the Service</li>
                <li>Communicate with you about your account</li>
                <li>Detect and prevent fraud or abuse</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2>4. Data Sharing</h2>
              <p>We do not sell your personal information. We may share data with:</p>
              <ul>
                <li>
                  <strong>Service Providers:</strong> Third parties that help us operate the
                  Service, including:
                  <ul>
                    <li>Stripe (payment processing)</li>
                    <li>Supabase (authentication and database)</li>
                    <li>Render (hosting)</li>
                    <li>PlateToVIN (vehicle data lookup)</li>
                  </ul>
                </li>
                <li>
                  <strong>Legal Requirements:</strong> When required by law, court order,
                  or government request
                </li>
                <li>
                  <strong>Business Transfers:</strong> In connection with a merger,
                  acquisition, or sale of assets
                </li>
              </ul>
            </section>

            <section>
              <h2>5. Data Retention</h2>
              <p>
                We retain your account information for as long as your account is active.
                Usage data is retained for up to 12 months for analytics and billing purposes.
                You may request deletion of your account and associated data at any time.
              </p>
            </section>

            <section>
              <h2>6. Data Security</h2>
              <p>
                We implement appropriate technical and organizational measures to protect
                your personal data, including:
              </p>
              <ul>
                <li>Encryption of data in transit (HTTPS/TLS)</li>
                <li>Secure password hashing</li>
                <li>Access controls and authentication</li>
                <li>Regular security assessments</li>
              </ul>
              <p>
                However, no method of transmission over the Internet is 100% secure.
                We cannot guarantee absolute security.
              </p>
            </section>

            <section>
              <h2>7. Your Rights</h2>
              <p>Depending on your location, you may have the right to:</p>
              <ul>
                <li>Access the personal data we hold about you</li>
                <li>Correct inaccurate personal data</li>
                <li>Request deletion of your personal data</li>
                <li>Object to or restrict processing of your data</li>
                <li>Data portability</li>
                <li>Withdraw consent at any time</li>
              </ul>
              <p>
                To exercise these rights, please contact us at privacy@wiredcop.com.
              </p>
            </section>

            <section>
              <h2>8. Cookies and Tracking</h2>
              <p>
                We use essential cookies and local storage to:
              </p>
              <ul>
                <li>Maintain your login session</li>
                <li>Remember your terms acceptance</li>
                <li>Store your preferences</li>
              </ul>
              <p>
                We do not use third-party tracking cookies or advertising cookies.
              </p>
            </section>

            <section>
              <h2>9. Children's Privacy</h2>
              <p>
                The Service is not intended for users under 18 years of age. We do not
                knowingly collect personal information from children. If we learn we have
                collected data from a child under 18, we will delete it promptly.
              </p>
            </section>

            <section>
              <h2>10. California Privacy Rights</h2>
              <p>
                If you are a California resident, you have additional rights under the
                California Consumer Privacy Act (CCPA), including the right to know what
                personal information we collect and the right to request deletion.
              </p>
            </section>

            <section>
              <h2>11. European Privacy Rights (GDPR)</h2>
              <p>
                If you are located in the European Economic Area (EEA), United Kingdom, or
                Switzerland, you have additional rights under the General Data Protection
                Regulation (GDPR):
              </p>
              <h3>Legal Basis for Processing</h3>
              <p>We process your personal data based on:</p>
              <ul>
                <li><strong>Contract performance:</strong> To provide the Service you signed up for</li>
                <li><strong>Legitimate interests:</strong> To improve our Service, prevent fraud, and ensure security</li>
                <li><strong>Consent:</strong> For optional features like location sharing, which you can withdraw at any time</li>
                <li><strong>Legal obligation:</strong> To comply with applicable laws</li>
              </ul>
              <h3>Your GDPR Rights</h3>
              <p>In addition to the rights listed in Section 7, you have the right to:</p>
              <ul>
                <li>Lodge a complaint with your local data protection authority</li>
                <li>Request restriction of processing</li>
                <li>Object to processing based on legitimate interests</li>
                <li>Not be subject to automated decision-making</li>
              </ul>
              <h3>International Data Transfers</h3>
              <p>
                Your data may be transferred to and processed in the United States, where our
                servers and service providers are located. We ensure appropriate safeguards are
                in place, including standard contractual clauses, to protect your data during
                such transfers.
              </p>
              <h3>Data Protection Officer</h3>
              <p>
                For GDPR-related inquiries, contact our data protection team at
                privacy@wiredcop.com.
              </p>
            </section>

            <section>
              <h2>12. Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you
                of material changes by posting the new policy on this page with an updated
                "Last updated" date. We encourage you to review this policy periodically.
              </p>
            </section>

            <section>
              <h2>13. Contact Us</h2>
              <p>
                For questions about this Privacy Policy or your personal data, contact us at:
              </p>
              <p>
                <strong>WiredCop LLC</strong><br />
                Email: privacy@wiredcop.com<br />
                Website: <a href="https://wiredcop.com" target="_blank" rel="noopener noreferrer">wiredcop.com</a>
              </p>
            </section>
          </div>

          <div className="legal-footer">
            <Link to="/" className="btn btn-dark">
              <ArrowLeft size={18} /> Back to App
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
